export type VastAd = {
  mediaUrl: string;
  clickThrough?: string;
  impressions: string[];
  clickTrackings: string[];
  errors: string[];
};

function extractUrl(node: Element | null): string {
  return node?.textContent?.trim() ?? "";
}

function collectUrls(doc: XMLDocument, selector: string): string[] {
  return Array.from(doc.querySelectorAll(selector))
    .map(extractUrl)
    .filter(Boolean);
}

/** Picks a browser-playable MediaFile, preferring mp4 over the first entry. */
function selectMediaUrl(doc: XMLDocument): string {
  const mediaFiles = Array.from(doc.querySelectorAll("MediaFile"));
  const playable =
    mediaFiles.find((node) =>
      node.getAttribute("type")?.toLowerCase().includes("mp4")
    ) ?? mediaFiles[0];
  return extractUrl(playable ?? null);
}

/** True when the URL is a safe http(s) destination to open. */
export function isHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/** Parses an IAB VAST tag into the fields needed to play a linear video ad. */
function parseVastAd(xml: string): VastAd | null {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  if (doc.querySelector("parsererror")) {
    return null;
  }

  const mediaUrl = selectMediaUrl(doc);
  if (!mediaUrl) {
    return null;
  }

  const clickThrough = extractUrl(doc.querySelector("ClickThrough"));
  return {
    mediaUrl,
    clickThrough: clickThrough || undefined,
    impressions: collectUrls(doc, "Impression"),
    clickTrackings: collectUrls(doc, "ClickTracking"),
    errors: collectUrls(doc, "Error"),
  };
}

/** Fetches a VAST tag, returning null when it carries no playable linear ad. */
export async function fetchVastAd(
  url: string,
  signal?: AbortSignal
): Promise<VastAd | null> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`VAST fetch failed with status ${response.status}`);
  }
  const xml = await response.text();
  return parseVastAd(xml);
}

/** Fires VAST tracking pixels fire-and-forget via image beacons. */
export function firePixels(urls: string[]): void {
  for (const url of urls) {
    new Image().src = url;
  }
}
